import { geoToWorld } from './projection.js';
import type { Alignment, DirectedPath } from './contracts.js';
export const ALIGNMENT_HEIGHT: Record<Alignment,number> = { tunnel: -2, openCut: -2, atGrade: 0, embankment: 1, elevated: 3 };
export const trackSpriteId = (alignment:Alignment):'track'|'track/deck' => alignment==='elevated'?'track/deck':'track';
export function alignmentAt(runs: readonly (readonly [number,Alignment])[], distance: number): Alignment {
  let result = runs[0]?.[1]; if (!result) throw new Error('alignment runs must not be empty');
  for (const [start, alignment] of runs) { if (distance < start) break; result = alignment; }
  return result;
}
export interface TrackSample { e: number; n: number; distance: number; heading: number; alignment: Alignment; height: number; pathId: string; physicalId?: string; /** Corridor samples mark trestle columns explicitly; path samples use every 40 m. */ column?: boolean }
interface CompiledPath { world: readonly [number, number][] }
const compiledPaths = new WeakMap<DirectedPath, CompiledPath>();
const compilePath = (path: DirectedPath): CompiledPath => {
  const cached = compiledPaths.get(path);
  if (cached) return cached;
  const compiled = { world: path.coordinates.map(geoToWorld) };
  compiledPaths.set(path, compiled);
  return compiled;
};
const segmentAt = (path: DirectedPath, distance: number): number => {
  let low = 1, high = path.sMeters.length - 1;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (path.sMeters[middle]! < distance) low = middle + 1;
    else high = middle;
  }
  return low;
};
const pointOnSegment = (path: DirectedPath, distance: number, i: number): { geo: [number,number]; heading: number } => {
  const a = path.coordinates[i - 1]!, b = path.coordinates[i]!, d0 = path.sMeters[i - 1]!, d1 = path.sMeters[i]!;
  const f = d1 === d0 ? 0 : (distance - d0) / (d1 - d0), world = compilePath(path).world;
  return { geo: [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f], heading: Math.atan2(world[i - 1]![1] - world[i]![1], world[i]![0] - world[i - 1]![0]) };
};
export function pointAlong(path: DirectedPath, distance: number): { geo: [number,number]; heading: number } {
  const length = path.sMeters.at(-1) ?? 0, d = Math.max(0, Math.min(length, distance));
  return pointOnSegment(path, d, segmentAt(path, d));
}
/**
 * `skip` drops samples before proximity clustering, so a skipped sample never absorbs a kept one. Samples every 10 m.
 */
/** Every 10 m from the path start, plus the path end. */
function evenSteps(length: number): number[] {
  const result: number[] = [];
  for (let d = 0; d < length; d += 10) result.push(d);
  result.push(length);
  return result;
}
export function buildTrack(paths: readonly DirectedPath[], skip?: (pathId: string, distance: number) => boolean): TrackSample[] {
  const clusterRadius = 6, cellSize = clusterRadius;
  const cells = new Map<string, TrackSample[]>(), result: TrackSample[] = [];
  const cell = (value: number) => Math.floor(value / cellSize);
  const headingDifference = (a: number, b: number) => {
    const difference = Math.abs(a - b) % Math.PI;
    return Math.min(difference, Math.PI - difference);
  };
  for (const path of paths) {
    const length = path.sMeters.at(-1)!; let segment = 1, run = 0;
    for (const d of evenSteps(length)) {
      while (segment < path.sMeters.length - 1 && path.sMeters[segment]! < d) segment += 1;
      while (run < path.runs.length - 1 && path.runs[run + 1]![0] <= d) run += 1;
      if (skip?.(path.id, d)) continue;
      const { geo, heading } = pointOnSegment(path, d, segment), [e,n] = geoToWorld(geo), alignment = path.runs[run]![1];
      let match: TrackSample | undefined;
      const cx = cell(e), cy = cell(n);
      for (let dx = -1; dx <= 1 && !match; dx += 1) for (let dy = -1; dy <= 1 && !match; dy += 1) {
        match = (cells.get(`${cx + dx}:${cy + dy}`) ?? []).find((anchor) =>
          anchor.alignment === alignment &&
          headingDifference(anchor.heading, heading) <= Math.PI / 16 &&
          Math.hypot(anchor.e - e, anchor.n - n) <= clusterRadius
        );
      }
      if (!match) {
        const sample = { e,n,distance:d,heading,alignment,height:ALIGNMENT_HEIGHT[alignment],pathId:path.id,physicalId:`${path.id}:${d}` };
        result.push(sample);
        const key = `${cx}:${cy}`, entries = cells.get(key) ?? [];
        entries.push(sample); cells.set(key, entries);
      }
    }
  }
  return result;
}
const orderedError = (live: readonly [number,number][], candidate: DirectedPath): number => {
  const world = compilePath(candidate).world;
  let minSegment = 0, previousS = -Infinity, total = 0;
  for (const point of live) {
    const p = geoToWorld(point); let best = Infinity, bestSegment = minSegment;
    let bestS = 0;
    for (let i = minSegment; i < candidate.coordinates.length - 1; i += 1) {
      const a = world[i]!, b = world[i + 1]!;
      const dx = b[0]-a[0], dy = b[1]-a[1], len2 = dx*dx+dy*dy;
      const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p[0]-a[0])*dx+(p[1]-a[1])*dy)/len2));
      const error = Math.hypot(p[0]-(a[0]+dx*t),p[1]-(a[1]+dy*t));
      const s = candidate.sMeters[i]! + (candidate.sMeters[i + 1]! - candidate.sMeters[i]!) * t;
      if (error < best) { best = error; bestSegment = i; bestS = s; }
    }
    if (bestS + 1e-6 < previousS) total += 1e9;
    previousS = Math.max(previousS, bestS); minSegment = bestSegment; total += best;
  }
  return total;
};
export function selectDirectedVariant(livePath: readonly [number,number][], variants: readonly DirectedPath[]): DirectedPath | undefined {
  let best: DirectedPath | undefined, bestError = Infinity;
  for (const candidate of variants) {
    const error = orderedError(livePath, candidate);
    if (error < bestError || (error === bestError && (!best || candidate.id.localeCompare(best.id) < 0))) {
      best = candidate; bestError = error;
    }
  }
  return best;
}
export interface PathMatch { distance:number; degraded:boolean }
export function projectMatchOnPath(point:[number,number],path:DirectedPath,previousDistance?:number):PathMatch{
  const p=geoToWorld(point), world=compilePath(path).world;
  let nearestError=Infinity,nearestDistance=Infinity,consistentError=Infinity,consistentDistance=Infinity;
  for(let i=1;i<world.length;i+=1){const a=world[i-1]!,b=world[i]!,dx=b[0]-a[0],dy=b[1]-a[1],l2=dx*dx+dy*dy,t=l2===0?0:Math.max(0,Math.min(1,((p[0]-a[0])*dx+(p[1]-a[1])*dy)/l2)),error=Math.hypot(p[0]-a[0]-dx*t,p[1]-a[1]-dy*t),distance=path.sMeters[i-1]!+(path.sMeters[i]!-path.sMeters[i-1]!)*t;
    if(error<nearestError||(error===nearestError&&distance<nearestDistance)){nearestError=error;nearestDistance=distance;}
    if(previousDistance!==undefined&&distance>=previousDistance-20&&distance<=previousDistance+200&&(error<consistentError||(error===consistentError&&distance<consistentDistance))){consistentError=error;consistentDistance=distance;}
  }
  if(nearestDistance===Infinity) throw new Error('path must contain at least two coordinates');
  if(previousDistance===undefined)return {distance:nearestDistance,degraded:false};
  return consistentDistance!==Infinity&&consistentError<=nearestError+40
    ?{distance:consistentDistance,degraded:false}
    :{distance:previousDistance,degraded:true};
}
export const projectDistanceOnPath=(point:[number,number],path:DirectedPath,previousDistance?:number):number=>projectMatchOnPath(point,path,previousDistance).distance;
